<template>
  <BittsLoading :is-loading="isInitialLoading">
    <div class="mcp-usage-table" data-testid="mcp-usage-table">
      <BittsEmptyState
        v-if="!rows.length"
        :font-awesome-icon="['far', 'users']"
        :font-awesome-icon-style="{ height: '26px', width: '26px' }"
        font-awesome-icon-class="empty-state-icon"
        title="No MCP usage yet"
        description="Users will appear here once they start using MCP tools."
      />
      <BittsTablePro
        v-else
        :columns="columns"
        :rows="rows"
        :row-height="44"
        :pagination="rows.length > 10"
        :pagination-page-size="10"
        :suppress-movable-columns="true"
      />
    </div>
  </BittsLoading>
</template>

<script setup lang="ts">
import { BittsEmptyState, BittsLoading, BittsTablePro } from '@crossbeam/bitts';

import { ColDef } from '@ag-grid-community/core';
import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';

import { McpUsageResponse } from '@/composables/mcp/useMcpUsage';
import { timeSince } from '@/date_time_utils';
import { formatNumberWithCommas } from '@/utils';

import McpUsageTableUserCellRenderer from './McpUsageTableUserCellRenderer.vue';

type UserRow = NonNullable<McpUsageResponse>['users'][number];

const { isLoading, data } = defineProps<{
  data: McpUsageResponse | null;
  isLoading: boolean;
}>();

const isInitialLoading = computed(() => isLoading && !data);

const lastRenderedRows = ref<UserRow[]>([]);
watch(
  () => data?.users,
  (newRows) => {
    if (newRows?.length) lastRenderedRows.value = newRows;
  },
  { immediate: true },
);

const rows = computed<UserRow[]>(() =>
  isLoading && !data?.users?.length
    ? lastRenderedRows.value
    : (data?.users ?? []),
);

const columns: ColDef<UserRow>[] = [
  {
    headerName: 'User',
    field: 'name',
    cellRenderer: McpUsageTableUserCellRenderer,
    flex: 2,
    minWidth: 200,
    sortable: true,
    resizable: false,
  },
  {
    headerName: 'Tool Calls',
    field: 'tool_calls',
    flex: 1,
    minWidth: 140,
    sortable: true,
    resizable: false,
    sort: 'desc',
    valueFormatter: (params) => formatNumberWithCommas(params.value ?? 0),
  },
  {
    headerName: 'Last Active',
    field: 'last_active',
    flex: 1,
    minWidth: 160,
    sortable: true,
    resizable: false,
    valueFormatter: (params) => {
      if (!params.value) return '—';
      const dt = DateTime.fromISO(params.value);
      return dt.isValid ? timeSince(dt) : '—';
    },
  },
];
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
:deep() .empty-state-icon {
  @apply text-neutral-text-weak p-20 rounded-[36px];
  background: radial-gradient(
      136.96% 69.92% at 50.09% -12.42%,
      theme(colors.neutral.accent / 0.2) 0%,
      theme(colors.neutral.accent / 0) 100%
    ),
    linear-gradient(0deg, theme(colors.white), theme(colors.white));
}
</style>
